import { parseGIF } from 'gifuct-js'

export const getStillDataUriFromGif = (gifUrl: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const image = new Image()

    const getStillDataUri = () => {
      const canvas = document.createElement('canvas')

      canvas.width = image.naturalWidth
      canvas.height = image.naturalHeight
      canvas.getContext('2d')?.drawImage(image, 0, 0)

      const stillDataUri = canvas.toDataURL('image/png')
      resolve(stillDataUri)
    }

    image.addEventListener('load', getStillDataUri)
    image.addEventListener('error', () => {
      // TODO: Monitor still gif creation error events in Datadog

      reject()
    })
    image.crossOrigin = 'anonymous'
    image.src = gifUrl
  })

/**
 * Extracts frame count from a GIF file using optimized parsing
 * This provides frame count without the expensive frame decompression
 *
 * Implementation Notes:
 * - Wrapped in Promise to handle requestIdleCallback's callback-based API
 * - Uses requestIdleCallback to avoid blocking the main thread during GIF parsing
 * - Only parses GIF structure (gif.frames.length) without decompressing pixel data
 * - This prevents browser lag that would occur with decompressFrames(gif, true)
 * - Falls back to setTimeout(0) for browsers without requestIdleCallback support
 */
export const getGifFrameCount = async (gifUrl: string): Promise<number> => {
  return new Promise((resolve) => {
    const processGif = async (): Promise<void> => {
      try {
        const response = await fetch(gifUrl)
        const arrayBuffer = await response.arrayBuffer()

        // Parse the GIF structure without decompressing frames
        const gif = parseGIF(arrayBuffer)

        // Count frames directly from the parsed structure without decompression
        resolve(gif.frames ? gif.frames.length : 1)
      } catch (error) {
        console.warn('Failed to extract GIF frame count:', error)
        resolve(1)
      }
    }

    // Use requestIdleCallback for better performance, fallback to setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => processGif(), { timeout: 5000 })
    } else {
      setTimeout(() => processGif(), 0)
    }
  })
}

const isEdge = () => window.navigator.userAgent.indexOf('Edge') > -1

export const generateThumbnailFromGif = (file: File | Blob): Promise<File> =>
  new Promise((resolve, reject) => {
    const image = new Image()

    const getStillDataUri = () => {
      const canvas = document.createElement('canvas')

      canvas.width = image.naturalWidth
      canvas.height = image.naturalHeight
      canvas.getContext('2d')?.drawImage(image, 0, 0)
      canvas.toBlob(
        (blob) => {
          let imageFile
          if (isEdge()) {
            // send as blob because Edge doesn't support file constructors. Currently
            // not resizing images sents as blobs because they appear incorrectly
            imageFile = blob
          } else {
            // TODO: use file name for image file
            const thumbnailName = 'image.jpeg'
            // const thumbnailName = file.name.replace(/\.[^/.]+$/, '.jpeg');
            if (blob) {
              imageFile = new File([blob], thumbnailName, { type: blob.type })
            }
          }
          // @ts-expect-error TS(2345) FIXME: Argument of type 'Blob | null | undefined' is not ... Remove this comment to see the full error message
          resolve(imageFile)
        },
        'image/jpeg',
        0.75 /* quality */,
      )
    }

    image.addEventListener('load', getStillDataUri)
    image.addEventListener('error', () => {
      // TODO: Monitor still gif creation error events in Datadog

      reject()
    })
    image.crossOrigin = 'anonymous'
    image.src = URL.createObjectURL(file)
  })
